import React, { useState } from "react"
import Sticky from "react-stickynode"
import styled from "styled-components"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { getApiUrlLocale } from "@/helpers"
import ResetCSS from "@/common/assets/css/style"
import NavbarNew from "@/containers/common/NavbarNew"
import Footer from "@/containers/common/FooterNew"
import MetaData from "@/common/components/MetaData"
import SectionHeading from "@/common/components/SectionHeading"
import Button from "@/common/components/Button"
import Container from "@/common/components/UI/Container"
import { AppWrapper, ContentWrapper } from "@/containers/common/style"
import { CLIENTS_DATA } from "../common/data/ClientDataNew"
import IconCard from "@/common/components/IconCard"

interface CompanyProps {
  activeTag: string
  showTag?: boolean
}

const Company = ({ activeTag, showTag }: CompanyProps) => {
  const parsedData = CLIENTS_DATA.filter(d => d.tag.includes(activeTag))
  return (
    <div className="partnersWrapper">
      <PartnersWrapper className="partnerCards">
        {parsedData.map(({ image, alt, tagD }, index) => (
          <IconCard
            key={`${alt}_${index}`}
            img={image}
            alt={alt}
            tagD={tagD}
            showTag={showTag}
          />
        ))}
      </PartnersWrapper>
    </div>
  )
}

interface CompanyTagProps {
  activeTag: string
  showTag?: boolean
}

const CompanyTag = ({ activeTag, showTag }: CompanyTagProps) => {
  const parsedData = CLIENTS_DATA.filter(d => d.tag.includes(activeTag))
  return (
    <CompanyTagWrapper className="partnersWrapper">
      <Container fullWidth className="partnerCards">
        {parsedData.map(({ image, alt, url, tagD }, index) => (
          <IconCard
            key={`${index}_${alt}`}
            img={image}
            alt={alt}
            url={url}
            tagD={tagD}
            showTag={showTag}
          />
        ))}
      </Container>
    </CompanyTagWrapper>
  )
}

const CompanyTagWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 80px 0;
  .partnerCards {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 52px;
    margin-bottom: 50px;
    .borderBox {
      flex: 1 1 50%;
      @media (max-width: 600px) {
        flex: 1 1 100%;
        padding: 30px 0px;
        &::before {
          width: 100%;
          height: 100%;
        }
      }
    }
    @media (max-width: 600px) {
      gap: 30px;
    }
  }
`

export const getStaticProps = async ({ locale }: { locale: string }) => {
  const res = await fetch(
    `${
      process.env.NEXT_PUBLIC_CMS_HOST
    }/api/media-coverages?populate=*&locale=${getApiUrlLocale(
      locale
    )}&sort[0]=date%3Adesc`,
    {
      headers: {
        "Cache-Control": "s-maxage=1, stale-while-revalidate"
      }
    }
  )
  const data = await res.json()
  if (!res.ok) {
    throw new Error(`Failed to fetch data, received status ${res.status}`)
  }

  return {
    props: {
      press: data,
      ...(await serverSideTranslations(locale, ["common", "contactUs"]))
    },
    revalidate: 300
  }
}

interface TabButtonsProps {
  tab: string
  id?: string
  title: string
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabButtons = ({
  tab,
  id,
  title,
  activeTab,
  setActiveTab
}: TabButtonsProps) => {
  return (
    <Button
      id={id}
      className="tabButton"
      type={activeTab === tab ? undefined : "secondary"}
      title={title}
      onClick={() => setActiveTab(tab)}
    />
  )
}

const Clients = () => {
  const { t } = useTranslation("common")
  const [activeTab, setActiveTab] = useState("ClientsAll")

  return (
    <div>
      <>
        <MetaData
          title={t("clients.meta.title")}
          description={t("clients.meta.description")}
          url={t("clients.meta.url")}
          image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
          languageAlternates={[
            [
              {
                hrefLang: "en-US",
                href: "https://www.kryptogo.com/clients"
              },
              {
                hrefLang: "zh-TW",
                href: "https://www.kryptogo.com/tw/clients"
              },
              {
                hrefLang: "zh-CN",
                href: "https://www.kryptogo.com/cn/clients"
              }
            ]
          ]}
        />

        <ResetCSS />
        <AppWrapper>
          <Sticky top={0} innerZ={1000} activeClass="sticky-active">
            <NavbarNew />
          </Sticky>
          <TitleWrapper>
            <TitleBG />
            <SectionHeading
              title={t("clients.title")}
              description={t("clients.desc")}
            />
          </TitleWrapper>
          <Container>
            <ContentWrapper className="mobileScoll">
              {/* <FilterWrapper> */}
              <ButtonWrapper>
                <TabButtons
                  id="ClientsAll"
                  title={t("clients.all")}
                  tab="ClientsAll"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabButtons
                  id="VASP"
                  title={t("clients.tag1")}
                  tab="VASP"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <TabButtons
                  id="Financial"
                  title={t("clients.tag2")}
                  tab="Financial"
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              </ButtonWrapper>

              {/* <Company activeTag={activeTab}/> */}
              {activeTab === "ClientsAll" && (
                <div>
                  <Company activeTag="Client" />
                </div>
              )}

              {activeTab === "VASP" && (
                <div>
                  <Company activeTag={"VASP"} />
                </div>
              )}
              {activeTab === "Financial" && (
                <div>
                  {/* <p className="center">{t("clients.tag2")}</p> */}
                  <CompanyTag showTag activeTag="Financials" />
                </div>
              )}
            </ContentWrapper>
          </Container>
          <Footer />
        </AppWrapper>
      </>
    </div>
  )
}

export default Clients

// >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
const ButtonWrapper = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  gap: 24px;

  @media (max-width: 800px) {
    justify-content: center !important;
    overflow: visible;
    flex-wrap: wrap;

    .tabButton {
      flex: 1 1 40%;
      padding: 10px 15px;
      min-width: 0px;
      background-color: none !important;
      box-shadow: none;
      transform: scale(1) !important;

      &:hover,
      &:active {
        box-shadow: none;
        transform: scale(1) !important;
      }

      &:nth-child(1) {
        flex: 1 1 100%;
      }
    }
  }
`
const TitleWrapper = styled.div`
  padding-top: 250px !important;
  padding-bottom: 100px !important;
  margin-bottom: -50px;
  position: relative;
  overflow: hidden;
  z-index: -1;

  h2,
  h4 {
    text-shadow: 0px 10px 20px $colorsBlack;
    max-width: 1200px !important;
    padding: 20px 20px 10px 20px;
    line-height: 1.5;
    font-weight: 600;
    letter-spacing: 0.36px;
    color: rgba(255, 255, 255, 0.8);
  }

  h2 {
    line-height: 2.5rem;
    font-weight: 700;
    font-size: 48px;
    line-height: 1.15;
    letter-spacing: 1;
    background: linear-gradient(to right, #ffffff, #ffc211);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow:
      0 0 20px rgba(255, 194, 17, 0.2),
      0 0 60px rgba(255, 194, 17, 0.5);
  }

  @media (max-width: 768px) {
    padding-top: 200px !important;

    h2 {
      font-size: 2rem;
    }

    h4 {
      font-size: 1rem;
      font-weight: 600;
    }
  }
  padding-top: 160px;
  @media (max-width: 768px) {
    padding-top: 100px;
  }
  @keyframes BannerSection {
    0% {
      background-size: 50%;
    }

    100% {
      background-size: 50%;
      background-position-x: 200%;
    }
  }
`

const TitleBG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  opacity: 0.5;
  background-size: cover;
  background-position: center;
  animation: BannerSection 100s infinite alternate;
  background:
    linear-gradient(#03122e, #03122e07 45%, #03122e 90%),
    url(/clients/clientsBanner.svg);
`

const Wrapper = styled.div`
  position: relative;
  padding: 120px 0;
  margin-top: -80px;

  @media (max-width: 768px) {
    padding: 60px 0;
    margin-top: -40px;
  }

  > div {
    > div {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      button {
        margin: 12px;
      }
    }
  }
`

const PartnersWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 2vh 0;
  .partnerCards {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 52px;
    margin-bottom: 50px;
  }
`
